import type { Project, UserGroup } from '../../types/config';
import './ProjectCard.css';

interface ProjectCardProps {
  project: Project;
  userGroups: UserGroup[];
  selectedUserGroupId?: string;
  onUserGroupSelect: (project: Project, userGroup: UserGroup) => void;
  isLoading?: boolean;
  hasLoadedGroups?: boolean;
}

export function ProjectCard({ 
  project, 
  userGroups, 
  selectedUserGroupId,
  onUserGroupSelect,
  isLoading = false,
  hasLoadedGroups = false
}: ProjectCardProps) {
  const handleClick = (userGroup: UserGroup) => {
    onUserGroupSelect(project, userGroup);
  };

  // If only one user group, auto-select it on card click
  const handleCardClick = () => {
    if (hasLoadedGroups && userGroups.length === 1) {
      handleClick(userGroups[0]);
    }
  };

  const showSingleGroupClickable = hasLoadedGroups && userGroups.length === 1;

  return (
    <div 
      className={`project-card ${showSingleGroupClickable ? 'single-group' : ''}`}
      onClick={showSingleGroupClickable ? handleCardClick : undefined}
      role={showSingleGroupClickable ? 'button' : undefined}
      tabIndex={showSingleGroupClickable ? 0 : undefined}
    >
      <div className="project-card-header">
        <div className="project-icon">
          <i className="fas fa-folder-open"></i>
        </div>
        <div className="project-info">
          <h3 className="project-name">{project._name}</h3>
          {project._shortName && (
            <span className="project-shortname">{project._shortName}</span>
          )}
        </div>
      </div>

      {project._description && (
        <p className="project-description">{project._description}</p>
      )}

      {isLoading ? (
        <div className="project-loading">
          <div className="project-loading-spinner">
            <div className="mini-spinner"></div>
          </div>
          <span>Loading user groups...</span>
        </div>
      ) : !hasLoadedGroups ? (
        <div className="project-click-hint">
          <i className="fas fa-mouse-pointer"></i>
          <span>Click to load user groups</span>
        </div>
      ) : userGroups.length > 1 ? (
        <div className="project-user-groups">
          <h4 className="user-groups-title">Select User Group</h4>
          <div className="user-groups-list">
            {userGroups.map(group => (
              <button
                key={group._id}
                className={`user-group-item ${selectedUserGroupId === group._id ? 'selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(group);
                }}
              >
                <i className="fas fa-users"></i>
                <span>{group._name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : userGroups.length === 1 ? (
        <div className="project-single-group">
          <i className="fas fa-users"></i>
          <span>{userGroups[0]._name}</span>
          <i className="fas fa-arrow-right"></i>
        </div>
      ) : (
        <div className="project-no-groups">
          <i className="fas fa-exclamation-triangle"></i>
          <span>No user groups available for this project</span>
        </div>
      )}
    </div>
  );
}

export default ProjectCard;
